/** In-page receipt upload queue: IDB-backed, 5m retry, single-flight. */

import { uploadReceipt, type Receipt } from './receiptApi';
import {
    deletePending,
    getPendingBlobs,
    listPendingForUser,
    putPendingWithBlobs,
    updatePendingMeta,
    type PendingReceiptRecord,
} from './receiptUploadStore';

export const RETRY_MS = 5 * 60 * 1000;
export const DRAIN_POLL_MS = 30_000;

export type EnqueueReceiptInput = {
    userId: number;
    files: File[];
    allocation_type: 'project_cost' | 'company_expense';
    project_id?: number | null;
    project_no?: number | null;
    transaction_id?: number | null;
    category?: string | null;
    subcategory?: string | null;
    merchant?: string | null;
    amount?: string | null;
    transaction_date?: string | null;
    responsible_user_id?: number | null;
    description?: string | null;
    notes?: string | null;
};

export type QueueSnapshot = {
    pending: PendingReceiptRecord[];
    uploadingLocalId: string | null;
    pausedForAuth: boolean;
};

type Listener = (snapshot: QueueSnapshot) => void;

export type UploadReceiptFn = (form: FormData) => Promise<Receipt>;

/** Pure helper — schedule next retry after a failed attempt. */
export function scheduleNextAttempt(now = Date.now()): number {
    return now + RETRY_MS;
}

/** Pure helper — items due for an upload attempt. */
export function selectDuePending(
    items: PendingReceiptRecord[],
    now = Date.now()
): PendingReceiptRecord[] {
    return items
        .filter(item => item.nextAttemptAt <= now)
        .sort((a, b) => a.nextAttemptAt - b.nextAttemptAt || a.createdAt - b.createdAt);
}

function displayFilename(files: File[]): string {
    if (files.length === 1) return files[0].name || 'receipt';
    return `receipt-${files.length}-pages`;
}

function buildFormData(
    record: PendingReceiptRecord,
    blobs: Array<{ blob: Blob; name: string; type: string }>
): FormData {
    const form = new FormData();
    for (const file of blobs) {
        form.append(
            'files',
            new File([file.blob], file.name, {
                type: file.type || file.blob.type || 'application/octet-stream',
            })
        );
    }
    form.append('allocation_type', record.allocation_type);
    if (record.transaction_id != null) {
        form.append('transaction_id', String(record.transaction_id));
    }
    if (record.allocation_type === 'project_cost') {
        if (record.project_id != null) {
            form.append('project_id', String(record.project_id));
        }
    } else {
        if (record.category) form.append('category', record.category);
        if (record.subcategory) form.append('subcategory', record.subcategory);
    }
    if (record.merchant) form.append('merchant', record.merchant);
    if (record.amount) form.append('amount', record.amount);
    if (record.transaction_date) {
        form.append('transaction_date', record.transaction_date);
    }
    if (record.responsible_user_id != null) {
        form.append('responsible_user_id', String(record.responsible_user_id));
    }
    if (record.description) form.append('description', record.description);
    if (record.notes) form.append('notes', record.notes);
    return form;
}

function isAuthError(error: unknown): boolean {
    if (!(error instanceof Error)) return false;
    const msg = error.message.toLowerCase();
    return (
        msg.includes('401') ||
        msg.includes('unauthorized') ||
        msg.includes('not authenticated') ||
        msg.includes('could not validate credentials')
    );
}

class ReceiptUploadQueue {
    private userId: number | null = null;
    private pending: PendingReceiptRecord[] = [];
    private uploadingLocalId: string | null = null;
    private pausedForAuth = false;
    private draining = false;
    private listeners = new Set<Listener>();
    private pollTimer: ReturnType<typeof setInterval> | null = null;
    private started = false;
    private uploadFn: UploadReceiptFn;

    constructor(uploadFn: UploadReceiptFn = uploadReceipt) {
        this.uploadFn = uploadFn;
    }

    /** Test seam: swap upload implementation. */
    setUploadFn(fn: UploadReceiptFn) {
        this.uploadFn = fn;
    }

    subscribe(listener: Listener): () => void {
        this.listeners.add(listener);
        listener(this.snapshot());
        return () => {
            this.listeners.delete(listener);
        };
    }

    snapshot(): QueueSnapshot {
        return {
            pending: this.pending.slice(),
            uploadingLocalId: this.uploadingLocalId,
            pausedForAuth: this.pausedForAuth,
        };
    }

    private emit() {
        const snap = this.snapshot();
        for (const listener of this.listeners) listener(snap);
    }

    private ensureListeners() {
        if (this.started || typeof window === 'undefined') return;
        this.started = true;
        window.addEventListener('online', this.onOnline);
        document.addEventListener('visibilitychange', this.onVisibility);
        this.pollTimer = setInterval(() => {
            void this.kick();
        }, DRAIN_POLL_MS);
    }

    private onOnline = () => {
        this.pausedForAuth = false;
        void this.kick();
    };

    private onVisibility = () => {
        if (document.visibilityState === 'visible') {
            void this.kick();
        }
    };

    async setUserId(userId: number | null) {
        this.ensureListeners();
        if (userId == null) {
            this.userId = null;
            this.pending = [];
            this.emit();
            return;
        }
        if (this.userId === userId && this.pending.length) {
            void this.kick();
            return;
        }
        this.userId = userId;
        this.pausedForAuth = false;
        this.pending = await listPendingForUser(userId);
        this.emit();
        void this.kick();
    }

    async enqueue(input: EnqueueReceiptInput): Promise<PendingReceiptRecord> {
        this.ensureListeners();
        if (!input.files.length) {
            throw new Error('Upload at least one receipt file.');
        }
        const localId =
            typeof crypto !== 'undefined' && crypto.randomUUID
                ? crypto.randomUUID()
                : `local-${Date.now()}-${Math.random().toString(16).slice(2)}`;
        const now = Date.now();
        const record: PendingReceiptRecord = {
            localId,
            userId: input.userId,
            createdAt: now,
            nextAttemptAt: now,
            lastError: null,
            original_filename: displayFilename(input.files),
            allocation_type: input.allocation_type,
            project_id: input.project_id ?? null,
            project_no: input.project_no ?? null,
            transaction_id: input.transaction_id ?? null,
            category: input.category ?? null,
            subcategory: input.subcategory ?? null,
            merchant: input.merchant ?? null,
            amount: input.amount ?? null,
            transaction_date: input.transaction_date ?? null,
            responsible_user_id: input.responsible_user_id ?? null,
            description: input.description ?? null,
            notes: input.notes ?? null,
            fileMeta: input.files.map(file => ({
                name: file.name,
                type: file.type,
            })),
        };
        await putPendingWithBlobs(
            record,
            input.files.map(file => ({
                blob: file,
                name: file.name,
                type: file.type,
            }))
        );
        this.userId = input.userId;
        this.pending = [
            record,
            ...this.pending.filter(item => item.localId !== localId),
        ].sort((a, b) => b.createdAt - a.createdAt);
        this.pausedForAuth = false;
        this.emit();
        void this.kick();
        return record;
    }

    async kick(): Promise<void> {
        if (this.draining || this.pausedForAuth || this.userId == null) return;
        if (typeof navigator !== 'undefined' && navigator.onLine === false) {
            return;
        }
        this.draining = true;
        try {
            while (true) {
                const due = selectDuePending(this.pending);
                const next = due[0];
                if (!next) break;
                await this.uploadOne(next);
                if (this.pausedForAuth) break;
            }
        } finally {
            this.draining = false;
        }
    }

    private async uploadOne(record: PendingReceiptRecord): Promise<void> {
        this.uploadingLocalId = record.localId;
        this.emit();
        try {
            const blobRows = await getPendingBlobs(record.localId);
            if (!blobRows.length) {
                throw new Error('Pending receipt images missing from device storage.');
            }
            const form = buildFormData(
                record,
                blobRows.map(row => ({
                    blob: row.blob,
                    name: row.name,
                    type: row.type,
                }))
            );
            await this.uploadFn(form);
            await deletePending(record.localId);
            this.pending = this.pending.filter(
                item => item.localId !== record.localId
            );
        } catch (error) {
            if (isAuthError(error)) {
                this.pausedForAuth = true;
                const message =
                    error instanceof Error
                        ? error.message
                        : 'Sign in to finish uploading';
                await updatePendingMeta(record.localId, {
                    lastError: message,
                    nextAttemptAt: scheduleNextAttempt(),
                });
                this.pending = this.pending.map(item =>
                    item.localId === record.localId
                        ? {
                              ...item,
                              lastError: message,
                              nextAttemptAt: scheduleNextAttempt(),
                          }
                        : item
                );
            } else {
                const message =
                    error instanceof Error ? error.message : 'Upload failed';
                const nextAttemptAt = scheduleNextAttempt();
                await updatePendingMeta(record.localId, {
                    lastError: message,
                    nextAttemptAt,
                });
                this.pending = this.pending.map(item =>
                    item.localId === record.localId
                        ? { ...item, lastError: message, nextAttemptAt }
                        : item
                );
            }
        } finally {
            this.uploadingLocalId = null;
            this.emit();
        }
    }
}

/** App-wide singleton — one drain loop for the dashboard tab. */
export const receiptUploadQueue = new ReceiptUploadQueue();

/** Factory for tests (isolated queue + injectable upload). */
export function createReceiptUploadQueue(uploadFn?: UploadReceiptFn) {
    return new ReceiptUploadQueue(uploadFn);
}
